import React, { useState, useEffect, useCallback } from 'react';
import BreadCrumb from 'Common/BreadCrumb';
import { ResourcesService } from '../../services/resourcesService';
import { ResourceModel, ResourceCollection } from '../../types/resource';
import AddResourceDialog from './AddResourceDialog';
import EditResourceDialog from './EditResourceDialog';
import DeleteResourceDialog from './DeleteResourceDialog';
import AddCollectionDialog from './AddCollectionDialog';
import DeleteCollectionDialog from './DeleteCollectionDialog';

import { Plus, ExternalLink, Trash2, Edit, Check, Package } from 'lucide-react';
import SimpleBar from "simplebar-react";

import { toast } from 'react-toastify';

const Resources = () => {
  const [resourceCollections, setResourceCollections] = useState<ResourceCollection[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  const [showAddResourceDialog, setShowAddResourceDialog] = useState(false);
  const [showEditResourceDialog, setShowEditResourceDialog] = useState(false);
  const [showDeleteResourceDialog, setShowDeleteResourceDialog] = useState(false);
  const [showAddCollectionDialog, setShowAddCollectionDialog] = useState(false);
  const [showDeleteCollectionDialog, setShowDeleteCollectionDialog] = useState(false);

  const [selectedResource, setSelectedResource] = useState<ResourceModel | null>(null);

  const loadResources = useCallback(async () => {
    try {
      setIsLoading(true);
      const collections = await ResourcesService.loadResources();
      setResourceCollections(collections);

      if (collections.length > 0 && !selectedCollectionId) {
        setSelectedCollectionId(collections[0].id);
      }
    } catch (error) {
      console.error('Error loading resources:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCollectionId]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  const handleCollectionSelect = (collectionId: string) => {
    setSelectedCollectionId(collectionId);
  };

  const selectedCollection = resourceCollections.find(
    collection => collection.id === selectedCollectionId
  );

  const handleAddCollection = () => {
    setShowAddCollectionDialog(true);
  };

  const handleAddResource = () => {
    if (selectedCollectionId) {
      setShowAddResourceDialog(true);
    }
  };

  const handleDeleteCollection = () => {
    if (selectedCollectionId) {
      setShowDeleteCollectionDialog(true);
    }
  };

  const handleDeleteResource = (resource: ResourceModel) => {
    setSelectedResource(resource);
    setShowDeleteResourceDialog(true);
  };

  const handleEditResource = (resource: ResourceModel) => {
    setSelectedResource(resource);
    setShowEditResourceDialog(true);
  };

  const handleSaveResource = async (resource: ResourceModel) => {
    try {
      await ResourcesService.saveResourceToCollection(resource, selectedCollectionId);
      await loadResources();
      toast.success('Resource added successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error saving resource:', error);
      toast.error('Failed to add resource. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw error;
    }
  };

  const handleUpdateResource = async (updatedResource: ResourceModel) => {
    try {
      if (selectedResource) {
        await ResourcesService.deleteResourceFromCollection(selectedResource, selectedCollectionId);
      }
      await ResourcesService.saveResourceToCollection(updatedResource, selectedCollectionId);
      await loadResources();
      toast.success('Resource updated successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error updating resource:', error);
      toast.error('Failed to update resource. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw error;
    }
  };

  const handleDeleteResourceConfirm = async () => {
    if (selectedResource) {
      try {
        await ResourcesService.deleteResourceFromCollection(selectedResource, selectedCollectionId);
        await loadResources();
        toast.success('Resource deleted successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } catch (error) {
        console.error('Error deleting resource:', error);
        toast.error('Failed to delete resource. Please try again.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        throw error;
      }
    }
  };

  const handleSaveCollection = async (collection: ResourceCollection) => {
    try {
      await ResourcesService.addCollection(collection);
      await loadResources();
      setSelectedCollectionId(collection.id);
      toast.success('Collection created successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error saving collection:', error);
      toast.error('Failed to create collection. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
      throw error;
    }
  };

  const handleDeleteCollectionConfirm = async () => {
    try {
      await ResourcesService.deleteCollection(selectedCollectionId);
      setSelectedCollectionId('');
      await loadResources();
      toast.success('Collection deleted successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error deleting collection:', error);
      toast.error('Failed to delete collection. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
      throw error;
    }
  };

  const handleCloseAddResourceDialog = () => setShowAddResourceDialog(false);
  const handleCloseEditResourceDialog = () => {
    setShowEditResourceDialog(false);
    setSelectedResource(null);
  };
  const handleCloseDeleteResourceDialog = () => {
    setShowDeleteResourceDialog(false);
    setSelectedResource(null);
  };
  const handleCloseAddCollectionDialog = () => setShowAddCollectionDialog(false);
  const handleCloseDeleteCollectionDialog = () => setShowDeleteCollectionDialog(false);

  const openResourceLink = (url: string) => {
    let fullUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      fullUrl = 'https://' + url;
    }
    window.open(fullUrl, '_blank');
  };

  return (
    <React.Fragment>
      {/* Desktop Breadcrumb */}
      <div className="hidden md:block">
        <BreadCrumb title='Resources' pageTitle='Dashboard' />
      </div>

      {/* Mobile Header with Breadcrumb and Title */}
      <div className="flex flex-row items-center justify-between gap-4 py-4 md:hidden">
        <h6 className="text-15 font-semibold">Resources</h6>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 dark:text-zink-400">Dashboard</span>
          <span className="text-slate-400 dark:text-zink-500">›</span>
          <span className="text-sm text-slate-500 dark:text-zink-400">Resources</span>
        </div>
      </div>

      <div className="grid grid-cols-12 xl:flex-row gap-x-5">
        <div className="col-span-12 lg:col-span-3 2xl:col-span-2 shrink-0 card">
          <div className="card-body">
            <button
              type="button"
              className="w-full text-white bg-red-500 border-red-500 btn hover:text-white hover:bg-red-600 hover:border-red-600 focus:text-white focus:bg-red-600 focus:border-red-600 focus:ring focus:ring-red-100 active:text-white active:bg-red-600 active:border-red-600 active:ring active:ring-red-100 dark:ring-custom-400/20"
              onClick={handleAddCollection}
            >
              <Plus className="inline-block size-4 mr-1" /> <span className="align-middle">Add Collection</span>
            </button>
            <ul className="mt-4 space-y-1">
              {resourceCollections.map((collection) => (
                <li key={collection.id}>
                  <button
                    className={`transition-all duration-300 ease-linear text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500 inline-flex items-center py-2 px-3 w-full text-left rounded-md hover:bg-slate-50 dark:hover:bg-zink-700 ${
                      selectedCollectionId === collection.id ? 'text-custom-500 dark:text-custom-500 bg-custom-50 dark:bg-custom-500/10' : ''
                    }`}
                    onClick={() => handleCollectionSelect(collection.id)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        {selectedCollectionId === collection.id ? (
                          <Check className="size-4 text-custom-500" />
                        ) : (
                          <Package className="size-4 text-slate-400 dark:text-zink-500" />
                        )}
                      </div>
                      <span className="align-middle truncate flex-1">{collection.category}</span>
                    </div>
                    <span className="flex-shrink-0 bg-slate-100 dark:bg-zink-600 text-slate-600 dark:text-zink-200 px-2 py-1 rounded-full text-xs ml-2">
                      {collection.resources.length}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-9 2xl:col-span-10 grow card">
          <div className="card-body">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <h6 className="text-15 font-semibold">
                  {selectedCollection ? selectedCollection.category : 'Resources'}
                </h6>
                {selectedCollection && (
                  <span className="bg-slate-100 dark:bg-zink-600 text-slate-600 dark:text-zink-200 px-2 py-1 rounded-full text-xs">
                    {selectedCollection.resources.length} resources
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={handleDeleteCollection}
                  className="text-white btn bg-red-500 border-red-500 hover:text-white hover:bg-red-600 hover:border-red-600 focus:text-white focus:bg-red-600 focus:border-red-600 focus:ring focus:ring-red-100 active:text-white active:bg-red-600 active:border-red-600 active:ring active:ring-red-100 dark:ring-red-400/20"
                  disabled={!selectedCollection}
                >
                  <Trash2 className="inline-block size-4 mr-1" /> <span className="align-middle hidden sm:inline">Delete Collection</span>
                </button>
                <button
                  onClick={handleAddResource}
                  className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                  disabled={!selectedCollection}
                >
                  <Plus className="inline-block size-4 mr-1" /> <span className="align-middle hidden sm:inline">Add Resource</span>
                </button>
              </div>
            </div>
          </div>

          <SimpleBar className="xl:max-h-[calc(100vh_-_300px)]">
            <div className="!pt-0 card-body">
              {isLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : !selectedCollection ? (
                <div className="text-center py-8">
                  <p className="text-slate-500 dark:text-zink-200">Please select a collection to view resources</p>
                </div>
              ) : selectedCollection.resources.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500 dark:text-zink-200">No resources found in this collection</p>
                </div>
              ) : (
                <div className="overflow-hidden">
                  {/* Mobile/Tablet View */}
                  <div className="block md:hidden space-y-4">
                    {selectedCollection.resources.map((resource, index) => (
                      <div key={index} className="bg-white dark:bg-zink-700 border border-slate-200 dark:border-zink-500 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h6 className="font-medium text-slate-800 dark:text-zink-100 break-words">
                              {resource.title}
                            </h6>
                            <p className="text-slate-600 dark:text-zink-300 mt-1 break-words">
                              {resource.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => openResourceLink(resource.url)}
                            className="text-custom-500 hover:text-custom-600 text-sm font-medium flex items-center gap-1"
                          >
                            <ExternalLink className="size-4" />
                            Open Link
                          </button>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditResource(resource)}
                              className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-blue-500 dark:hover:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-500/20"
                              title="Edit resource"
                            >
                              <Edit className="size-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteResource(resource)}
                              className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-red-500 dark:hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20"
                              title="Delete resource"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop View */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600">
                        <tr>
                          <th className="px-4 py-3 first:pl-6 last:pr-6 font-semibold border-y border-slate-200 dark:border-zink-500 text-left w-80">
                            Title
                          </th>
                          <th className="px-4 py-3 first:pl-6 last:pr-6 font-semibold border-y border-slate-200 dark:border-zink-500 text-left">
                            Description
                          </th>
                          <th className="px-4 py-3 first:pl-6 last:pr-6 font-semibold border-y border-slate-200 dark:border-zink-500 text-center w-32">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCollection.resources.map((resource, index) => (
                          <tr key={index} className="border-t border-slate-200 dark:border-zink-500 hover:bg-slate-50 dark:hover:bg-zink-700/50">
                            <td className="px-4 py-3 first:pl-6 last:pr-6 w-80">
                              <div className="break-words">
                                {resource.title}
                              </div>
                            </td>
                            <td className="px-4 py-3 first:pl-6 last:pr-6">
                              <div className="break-words">
                                {resource.description}
                              </div>
                            </td>
                            <td className="px-4 py-3 first:pl-6 last:pr-6">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => openResourceLink(resource.url)}
                                  className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-100 dark:hover:bg-custom-500/20"
                                  title="Open link"
                                >
                                  <ExternalLink className="size-4" />
                                </button>
                                <button
                                  onClick={() => handleEditResource(resource)}
                                  className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-blue-500 dark:hover:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-500/20"
                                  title="Edit resource"
                                >
                                  <Edit className="size-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteResource(resource)}
                                  className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-red-500 dark:hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20"
                                  title="Delete resource"
                                >
                                  <Trash2 className="size-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </SimpleBar>
        </div>
      </div>

      {selectedCollection && (
        <AddResourceDialog
          collectionName={selectedCollection.category}
          onClose={handleCloseAddResourceDialog}
          onSave={handleSaveResource}
          show={showAddResourceDialog}
        />
      )}

      {selectedResource && (
        <EditResourceDialog
          resource={selectedResource}
          onClose={handleCloseEditResourceDialog}
          onSave={handleUpdateResource}
          show={showEditResourceDialog}
        />
      )}

      {selectedResource && (
        <DeleteResourceDialog
          resource={selectedResource}
          onClose={handleCloseDeleteResourceDialog}
          onDelete={handleDeleteResourceConfirm}
          show={showDeleteResourceDialog}
        />
      )}

      <AddCollectionDialog
        onClose={handleCloseAddCollectionDialog}
        onSave={handleSaveCollection}
        show={showAddCollectionDialog}
        lastOrder={resourceCollections.length > 0 ? Math.max(...resourceCollections.map(c => c.order)) : 0}
      />

      {selectedCollection && (
        <DeleteCollectionDialog
          collectionName={selectedCollection.category}
          onClose={handleCloseDeleteCollectionDialog}
          onDelete={handleDeleteCollectionConfirm}
          show={showDeleteCollectionDialog}
        />
      )}
    </React.Fragment>
  );
};

export default Resources;
